<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        All groups
      </h2>
    </div>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <template v-else-if="groups.length > 0">
        <div class="groups-list">
          <div
            v-for="(group, index) in visibleGroups"
            :key="group.conversation_id || index"
            class="group-item"
            @click="handleGroupClick(group)"
          >
            <div class="group-image">
              <img
                v-if="group.avatar"
                :src="group.avatar"
                :alt="group.name"
              >
              <div
                v-else
                class="image-placeholder"
              >
                {{ getInitials(group.name) }}
              </div>
            </div>
            <div class="group-info">
              <span class="group-name">{{ group.name || 'Unnamed Group' }}</span>
              <span class="group-meta">{{ getMessageSnippet(group) }}</span>
            </div>
            <span
              :class="group.joined ? 'joined-chip' : 'not-joined-chip'"
            >{{ group.joined ? 'Joined' : 'Not joined' }}</span>
          </div>
        </div>
        <button
          v-if="hasMore"
          class="load-more-btn"
          @click="loadMore"
        >
          Load more
        </button>
      </template>
      <div
        v-else
        class="panel-empty"
      >
        No group conversations
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted } from 'vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoDiscoverGroupsData } from '../services/demoData.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { pushConversationPanel } from '../composables/useOptixPanel.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'
import { useDemoMode } from '../composables/useDemoMode.js'

defineEmits(['close'])

const organizationId = computed(() => getOrganizationIdFromURL())

const allGroupsQuery = `
  query AllPublicGroups(
    $organizationId: ID
    $includeJoinable: [ConversationType!]
    $conversationType: [ConversationType!]
  ) {
    conversations(
      organization_id: $organizationId
      include_joinable: $includeJoinable
      conversation_type: $conversationType
    ) {
      total
      data {
        conversation_id
        name
        message_count
        latest_message {
          user_id
          message
          plaintext
          timestamp
        }
        image {
          url
        }
        conversation_type
        users
      }
    }
  }
`

const { data, loading, error, cleanup } = useGraphQLData({
  dataKey: 'all_public_groups',
  query: allGroupsQuery,
  operationName: 'AllPublicGroups',
  variables: computed(() => ({
    organizationId: organizationId.value || undefined,
    includeJoinable: ['PUBLIC_GROUP'],
    conversationType: ['PUBLIC_GROUP']
  })),
  priority: 1,
  getDemoData: () => getDemoDiscoverGroupsData()
})

const demoMode = useDemoMode()

const { data: meData } = useGraphQLData({
  dataKey: 'all_groups_current_user',
  query: `query GetCurrentUser { me { user { user_id } } }`,
  operationName: 'GetCurrentUser',
  variables: {},
  priority: 10,
  enableBatching: true,
  autoFetch: true
})

const currentUserId = computed(() => meData.value?.me?.user?.user_id || '')

const groups = computed(() => {
  const items = data.value?.conversations?.data ?? []
  return items.map((item) => {
    const users = Array.isArray(item.users) ? item.users : []
    const joined = demoMode?.value === true
      ? item.joined ?? false
      : currentUserId.value && users.some(u => String(u) === String(currentUserId.value))
    return {
      conversation_id: item.conversation_id,
      name: item.name || 'Unnamed Group',
      avatar: getAvatarUrl(item.image?.url, item.name || item.conversation_id),
      messageCount: item.message_count || 0,
      latest_message: item.latest_message || null,
      memberCount: users.length,
      joined
    }
  })
})

const PAGE_SIZE = 20
const visibleCount = ref(PAGE_SIZE)
const visibleGroups = computed(() => groups.value.slice(0, visibleCount.value))
const hasMore = computed(() => visibleCount.value < groups.value.length)
const loadMore = () => { visibleCount.value += PAGE_SIZE }

const getMessageSnippet = (group) => {
  const msg = group.latest_message
  if (!msg) return 'No messages yet'
  return (msg.plaintext || msg.message || '').trim() || 'No messages yet'
}

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  return words.length >= 2 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : name.charAt(0).toUpperCase()
}

const { guard } = useTrialModeGuard()
const handleGroupClick = guard((group) => {
  if (!group?.conversation_id) return
  pushConversationPanel(group.conversation_id)
})

onUnmounted(() => { cleanup() })
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.groups-list {
  display: flex;
  flex-direction: column;
}

.group-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s;
}

.group-item:last-child {
  border-bottom: none;
}

.group-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.group-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.group-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.group-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.group-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-meta {
  display: block;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.joined-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
  background-color: #DBEDDB;
  color: #1C3829;
}

.not-joined-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
  background-color: #f3f4f6;
  color: rgba(0, 0, 0, 0.6);
}

.load-more-btn {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: white;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.load-more-btn:hover {
  background: #f3f4f6;
}
</style>
